import React, { useState, useEffect, useRef } from 'react';
import { Menu, MenuItem, Badge, IconButton } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import api from '../api/api';
import { formatDistanceToNow } from 'date-fns';


const PAGE_SIZE = 15;

const NotificationDropdown = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const listRef = useRef();

    // Fetch notifications on mount
    useEffect(() => {
        fetchNotifications(1, true);

        const refreshHandler = () => {
            fetchNotifications(1, true);
        };

        window.addEventListener('new-notification', refreshHandler);
        return () => window.removeEventListener('new-notification', refreshHandler);
        // eslint-disable-next-line
    }, []);

    // Count unread notifications
    const unreadCount = notifications.filter(n => !n.read_at).length;
    const unreadCountDisplay = unreadCount > 10 ? "+10" : unreadCount;
    
    const alertStyles = {
      info: { bg: 'bg-blue-100', text: 'text-blue-600', hover: '#BFDBFE40' },
      warning: { bg: 'bg-yellow-100', text: 'text-yellow-600', hover: '#FEF3C740' },
      danger: { bg: 'bg-red-100', text: 'text-red-600', hover: '#FECACA40' },
      success: { bg: 'bg-green-100', text: 'text-green-600', hover: '#BBF7D040' },
    };

    const alertIcons = {
      info: 'ℹ️',
      warning: '⚠️',
      danger: '❌',
      success: '✅',
    };

    const fetchNotifications = async (pageNum = 1, initial = false) => {
        if (initial) setLoading(true);
        else setFetchingMore(true);
        try {
            const res = await api.get('/notifications', { params: { page: pageNum, per_page: PAGE_SIZE } });
            const newNotifications = res.data || [];
            const meta = res.meta || {};
            if (pageNum === 1) {
                setNotifications(newNotifications);
            } else {
                setNotifications(prev => [...prev, ...newNotifications]);
            }
            // Pagination: agar current_page < last_page toh aur bhi notifications hain
            setHasMore(meta.current_page < meta.last_page);
            setPage(meta.current_page || pageNum);
        } catch (e) {
            if (pageNum === 1) setNotifications([]);
            setHasMore(false);
        }
        setLoading(false);
        setFetchingMore(false);
    };

    const markAllRead = async () => {
        try {
            await api.post('/notifications/mark-all-read');
            setNotifications(notifications => notifications.map(n => ({ ...n, read_at: new Date().toISOString() })));
        } catch (e) { }
    };

    const handleOpen = async (event) => {
        setAnchorEl(event.currentTarget);
        await markAllRead();
    };

    const handleClose = () => setAnchorEl(null);

    // Infinite scroll handler
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (!fetchingMore && hasMore && scrollHeight - scrollTop - clientHeight < 50) {
            fetchNotifications(page + 1);
        }
    };

    return (
        <div className="relative">
            <IconButton
                onClick={handleOpen}
                className="relative p-2 sm:p-3 bg-gradient-to-br from-accent to-primary text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
                <Badge
                    badgeContent={unreadCountDisplay}
                    color="error"
                    overlap="circular"
                    className="font-bold"
                >
                    <NotificationsIcon className="text-lg sm:text-2xl text-white" />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    elevation: 3,
                    className: "mt-2",
                    sx: {
                        // Mobile: full screen
                        '@media (max-width: 640px)': {
                            width: '92vw',
                            height: '88vh',
                            maxWidth: '92vw',
                            maxHeight: '88vh',
                            left: 0,
                            top: 0,
                            borderRadius: 4,
                            position: 'fixed',
                            m: 0,
                            p: 0,
                            zIndex: 1300,
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.2)', // subtle overlay
                        },

                        width: '320px',
                        maxWidth: '90vw',
                        borderRadius: '15px',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    },

                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <div className="sm:hidden flex flex-col items-center pt-3 pb-1 relative bg-white">
                    <div className="w-12 h-1.5 rounded-full bg-gray-300 mb-2" />
                    <button
                        onClick={handleClose}
                        className="absolute right-4 top-2 text-gray-400 hover:text-gray-600 text-2xl"
                        aria-label="Close"
                        style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1 }}
                    >
                        &times;
                    </button>
                </div>
                <div className="bg-gradient-to-r from-primary to-accent p-4">
                    <h3 className="text-white font-semibold">Notifications</h3>
                </div>

                {/* Mobile drag handle and close button */}
                <div
                    ref={listRef}
                    onScroll={handleScroll}
                    className="
                                overflow-y-auto
                                bg-white
                                px-0
                                pt-0
                                pb-4
                                w-full
                                max-h-[calc(100vh-110px)]
                                md:max-h-[400px]
                                "
                    style={{
                        maxWidth: '100vw',
                        background: '#fff',
                        paddingBottom: 16,
                        paddingTop: 0,
                    }}
                >
                    {loading ? (
                        <div className="p-4 text-center text-gray-500">Loading...</div>
                    ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">No notifications</div>
                    ) : (
                        notifications.map((n, idx) => {
                            // Get styles based on alert_level
                            const style = alertStyles[n.alert_level] || { bg: 'bg-gray-100', text: 'text-gray-600' };
                            // Get icon based on alert_level; if n.emoji exists use it.
                            const icon = n.emoji || alertIcons[n.alert_level] || '🔔';

                            return (
                                <MenuItem
                                    key={n.id || idx}
                                    className={`border-b w-full transition-colors duration-200 ${
                                      !n.read_at
                                        ? 'bg-purple-50 font-semibold text-purple-900'
                                        : 'bg-white'
                                    }`}
                                    onClick={handleClose}
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: !n.read_at ? undefined : style.hover, // change this value to your desired hover color
                                      },
                                      borderLeft: !n.read_at ? '4px solid #a78bfa' : '4px solid transparent',
                                      alignItems: 'flex-start',
                                    }}
                                >
                                    <div className="py-3 w-full">
                                        <div className="flex items-start gap-3">
                                            <div className={`${style.bg} p-2 rounded-full flex-shrink-0`}>
                                                <span className="text-xl">{icon}</span>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`font-medium whitespace-normal break-words ${style.text}`}>
                                                  {n.title}
                                                </p>
                                                <p className="text-sm text-gray-600 whitespace-normal break-words">
                                                  {n.body}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </MenuItem>
                            );
                        })
                    )}
                    {fetchingMore && (
                        <div className="p-2 text-center text-gray-400 text-xs">Loading more...</div>
                    )}
                    {!hasMore && notifications.length > 0 && (
                        <div className="p-2 text-center text-gray-400 text-xs">No more notifications</div>
                    )}
                </div>
            </Menu>
        </div>
    );
};

export default NotificationDropdown;